/**
 * Formatting helpers used across the app. Keep these pure — no React,
 * no I/O — so they can be tested standalone and reused freely.
 */

/**
 * "Just now / 5m ago / 3h ago / 2d ago / 03/15/2025" — bucketed by
 * minute / hour / day, falling back to locale date string beyond a
 * week. Same algorithm in iOS RecentsView's row rendering; kept
 * client-side because timezone display should follow the user, not
 * the server.
 */
export function relativeTime(iso: string): string {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

/** Short calendar label like "May 23" — for chart axis ticks. Follows the
 *  user's locale + timezone. */
export function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

/** Full calendar label like "May 23, 2026" — for detail/admin contexts where
 *  the year matters. The single source for full-date display. */
export function fullDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Locale-aware integer with thousands separators ("12,500"). The single
 *  source for plain number display (XP, counts, etc.). */
export function formatNumber(n: number): string {
  return Math.round(n).toLocaleString();
}

/** Big-blinds formatter — rounded to one decimal, "bb" suffix. */
export function formatBb(amount: number): string {
  const rounded = Math.round(amount * 10) / 10;
  return `${rounded}bb`;
}
